import { action, observable, runInAction, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import axios, { AxiosError, CancelTokenSource } from 'axios';

import { reverse } from 'named-urls';

import Logger from 'utils/logger';

import API from 'utils/api';
import { EventType } from 'utils/events/constants';
import { UuidableName } from 'utils/api/types';
import { AnswerTypes, SchemaContent } from 'utils/api/modules/incidents/incidents.types';
import {
  EntityValueType,
  EntityDetailsType,
  EntityFormType,
  AmendmentSummaryType,
  SubmissionVersionType,
  EntityHistoryResponseType,
} from 'utils/api/modules/entities/entities.types';

import { isNotFound } from 'utils/api/errors';

import { EntityModules, UserRole } from 'core/constants';

import { postMessage } from 'utils/events/broadcast';

import routes from 'core/routes';

import EntitiesActivities from 'stores/EntitiesActivities';

import { PublicSubmissionUserDetails } from 'stores/EntitiesFormSubmission/EntitiesFormSubmission';

import { PipelineDetailsType } from 'stores/PipelineDetails/PipelineDetails';

import RootStore from '../Root';
import EntityDetails, { AMENDMENT_KEY } from '../EntityDetails/EntityDetails';
import ReporterActivities from './ReporterActivities';
import { findFieldByRoles, handleEditError } from './helpers';

export { CREATED_AT_KEY, IMPORTED_KEY, AMENDMENT_KEY } from '../EntityDetails/EntityDetails';

const PIPELINE_STAGE_ROLE = 'pipeline_stage';

export type EventDetailsType = EntityDetailsType & {
  submission: {
    form: EntityFormType;
    content: Record<string, AnswerTypes>;
    stack: string[];
    publicSubmissionDetails?: PublicSubmissionUserDetails;
    offlineSubmission?: boolean;
    imported: boolean;
  };
  legacySubmission: SchemaContent | null;
  amendmentSummary?: AmendmentSummaryType | null;
};

export default class EventDetailsStore extends EntityDetails<EventDetailsType> {
  @observable reporterActivities?: ReporterActivities;

  @observable updatingCollaborators = false;

  @observable pipeline?: PipelineDetailsType;

  @observable pipelines: PipelineDetailsType[] = [];

  @observable stageField?: {
    uuid: string;
    key: string;
  };

  @observable submissionVersions?: SubmissionVersionType[];

  @observable loadingSubmissionVersions = false;

  @observable submissionVersionsError = false;

  private submissionVersionsFor?: string;

  private submissionVersionsRequest?: CancelTokenSource;

  private amendmentHistoryRequest?: CancelTokenSource;

  protected supportsHistoryImport = true;

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api, EntityModules.Events, routes.dashboard.events.toString(), (uuid: string): string =>
      reverse(routes.dashboard.events.details, { entityId: uuid })
    );

    makeObservable(this);

    eventBus.register(this);
  }

  protected getCreationFormName(): string | null {
    return this.details?.submission?.form.name || null;
  }

  protected wasImported(): boolean {
    return Boolean(this.details?.submission?.imported);
  }

  protected async loadAmendmentHistoryEntries(entityId: string): Promise<EntityHistoryResponseType[]> {
    if (!this.details?.amendmentSummary) {
      return [];
    }

    this.amendmentHistoryRequest?.cancel();
    const cancelTokenSource = axios.CancelToken.source();
    this.amendmentHistoryRequest = cancelTokenSource;

    try {
      const {
        data: { results },
      } = await this.api.entities.loadSubmissionVersions(this.entityType, entityId, cancelTokenSource)();

      return results
        .filter((version) => !version.isFirst && version.attribution === 'partner' && version.source)
        .map((version) => ({
          createdAt: version.currentFrom,
          createdBy: null,
          current: version.source,
          previous: null,
          entityField: {
            key: AMENDMENT_KEY,
            uuid: version.uuid,
          },
        }));
    } catch (e) {
      if (axios.isCancel(e)) {
        return [];
      }
      Logger.error('Failed to load submission amendments for history', e);
      return [];
    }
  }

  @action.bound
  async loadDetails(eventId: string): Promise<void> {
    this.isLoaded = false;
    try {
      const { data } = await this.api.entities.loadEntitiesDetails<EventDetailsType>(this.entityType, eventId)();

      // only load pipeline when user has role different from user
      if (this.store.session.user?.role !== UserRole.User) {
        await this.loadPipeline(eventId);
        await this.loadPipelines();
      }

      runInAction(() => {
        this.activities = new EntitiesActivities(this.store, this.api, eventId, this.entityType);
        this.reporterActivities = new ReporterActivities(this.store, this.api, eventId);
        this.error = undefined;
        this.details = data;

        this.stageField = findFieldByRoles(this.store.entityFields.fields, [PIPELINE_STAGE_ROLE])[0]?.[1];
      });
    } catch (e) {
      const err = e as AxiosError;
      runInAction(() => {
        this.error = err;
      });
      if (!isNotFound(err)) {
        Logger.error(`Invalid load event details API response. Task id: ${eventId}`, err);
      }
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  @action.bound
  async loadSubmissionVersions(eventId: string): Promise<void> {
    // EventDetailsStore is a RootStore singleton reused across records, so a response for the
    // previous event can land after the reader has navigated. Drop whatever belongs to another
    // record before fetching, and check again before assigning.
    if (this.submissionVersionsFor !== eventId) {
      this.submissionVersions = undefined;
      this.submissionVersionsFor = eventId;
    }

    this.submissionVersionsRequest?.cancel();
    const cancelTokenSource = axios.CancelToken.source();
    this.submissionVersionsRequest = cancelTokenSource;

    this.loadingSubmissionVersions = true;
    this.submissionVersionsError = false;

    try {
      const {
        data: { results },
      } = await this.api.entities.loadSubmissionVersions(this.entityType, eventId, cancelTokenSource)();

      runInAction(() => {
        if (this.submissionVersionsFor !== eventId) {
          return;
        }
        this.submissionVersions = results;
      });
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }
      Logger.error('Failed to load submission versions', e);
      runInAction(() => {
        if (this.submissionVersionsFor !== eventId) {
          return;
        }
        this.submissionVersionsError = true;
      });
    } finally {
      runInAction(() => {
        if (this.submissionVersionsRequest === cancelTokenSource) {
          this.submissionVersionsRequest = undefined;
          this.loadingSubmissionVersions = false;
        }
      });
    }
  }

  create = async (eventName: string): Promise<boolean> => {
    try {
      await this.api.entities.createEntity(this.entityType, { [`${this.prefix}name`]: eventName })();
      return true;
    } catch (e) {
      Logger.error('Failed to create event', e);

      return false;
    }
  };

  delete = async (eventId: string): Promise<boolean> => {
    try {
      await this.api.entities.deleteEntity(this.entityType, eventId)();
    } catch (e) {
      Logger.error('Failed to delete event', e);
      return false;
    }
    return true;
  };

  edit = async (data: Record<string, EntityValueType>): Promise<boolean | string[]> => {
    if (this.details?.uuid === undefined) {
      return false;
    }
    try {
      const edited = await this.api.entities.editEntity<EventDetailsType>(this.entityType, this.details?.uuid, data)();
      this.details = edited.data;
      this.isContentEdited = false;
      postMessage(EventType.UpdatedEvent, { uuid: edited.data.uuid });
      this.store.notification.enqueueSuccessSnackbar('Event edited successfully');
      return true;
    } catch (e) {
      const error = e as AxiosError<string[] | { entityFieldValues?: string[] }>;
      const errorFieldKeys = handleEditError(error, this.details, this.store.notification.enqueueErrorSnackbar);

      // If we have field-specific errors, return them for form highlighting
      if (errorFieldKeys.length > 0) {
        return errorFieldKeys;
      }

      return false;
    }
  };

  @action.bound
  async loadCount(): Promise<number> {
    this.isLoaded = false;
    try {
      const { data } = await this.api.entities.loadEntitiesCount(EntityModules.Events)();
      runInAction(() => {
        this.error = undefined;
      });
      return data.count;
    } catch (e) {
      runInAction(() => {
        // @ts-ignore
        this.error = e;
      });
      return 0;
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  @action.bound
  async updateCollaborators(user: UuidableName): Promise<void> {
    if (!this.details) {
      return;
    }

    this.updatingCollaborators = true;
    try {
      const { data } = await this.api.entities.updateEntityCollaborators(this.entityType, this.details.uuid, {
        uuid: user.uuid,
      })();
      this.details.collaborators = data;

      this.store.notification.enqueueSuccessSnackbar(`${user.name} has been added as a collaborator`);
    } catch (e) {
      Logger.error('Failed to update event collaborators', e);
      this.store.notification.enqueueErrorSnackbar(`Something went wrong. Please try again.`);
    } finally {
      this.updatingCollaborators = false;
    }
  }

  @action.bound
  async deleteCollaborator(user: UuidableName): Promise<void> {
    if (!this.details) {
      return;
    }

    try {
      await this.api.entities.deleteEntityCollaborator(this.entityType, this.details.uuid, {
        uuid: user.uuid,
      })();

      this.details.collaborators = (this.details.collaborators || []).filter(
        (collaborator) => collaborator.uuid !== user.uuid
      );

      this.store.notification.enqueueWarningSnackbar(`${user.name} has been removed as a collaborator`);
    } catch (e) {
      Logger.error('Failed to delete event collaborator', e);
      this.store.notification.enqueueErrorSnackbar(`Something went wrong. Please try again.`);
    }
  }

  // pipelines and stages
  @action.bound
  async loadPipelines(): Promise<void> {
    try {
      const { data } = await this.api.entities.loadEntityPipelines(this.entityType)();
      this.pipelines = data.results;
    } catch (e) {
      Logger.error('Failed to load pipelines', e);
      this.pipelines = [];
    }
  }

  @action.bound
  async loadPipeline(eventId: string): Promise<void> {
    try {
      const { data } = await this.api.entities.loadEntityPipeline(this.entityType, eventId)();
      this.pipeline = data;
    } catch (e) {
      Logger.error('Failed to load pipeline', e);
    }
  }

  @action.bound
  async updateStage(stageId: string): Promise<void> {
    if (!this.details) return;

    try {
      await this.api.entities.updateEntityStage(this.entityType, this.details?.uuid, {
        uuid: stageId,
      })();

      // reload event details
      await this.loadDetails(this.details.uuid);

      this.store.notification.enqueueSuccessSnackbar('Stage changed successfully');
    } catch (e) {
      const error = e as AxiosError<{ error?: string; message?: string }>;
      Logger.error('Failed to update event stage', e);

      if (error.response?.data?.error === 'missing_required_fields' && error.response?.data?.message) {
        this.store.notification.enqueueErrorSnackbar(error.response.data.message);
      } else {
        this.store.notification.enqueueErrorSnackbar('Failed to change stage. Please try again.');
      }
    }
  }

  @action.bound
  async updatePipeline(pipelineId: string): Promise<void> {
    if (!this.details) return;
    try {
      await this.api.entities.updateEntityPipeline(this.entityType, this.details?.uuid, {
        uuid: pipelineId,
      })();

      // reload event details only when edit succeeds
      await this.loadDetails(this.details.uuid);

      this.store.notification.enqueueSuccessSnackbar('Pipeline updated successfully');
    } catch (e) {
      Logger.error('Failed to update event pipeline', e);
      this.store.notification.enqueueErrorSnackbar('Failed to update pipeline. Please try again.');
    }
  }

  @action.bound
  contentHasBeenEdited(questionId: string, newValue: AnswerTypes): void {
    const question = this.details?.entity[questionId];
    if (question?.value !== newValue) {
      this.isContentEdited = true;
    } else {
      this.isContentEdited = false;
    }
  }

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.isLoaded = false;
    this.error = undefined;
    this.details = undefined;
    this.historyLoaded = false;
    this.initialHistory = undefined;
    this.historyHasMore = false;
    this.loadingHistory = false;
    this.pipelines = [];
    this.pipeline = undefined;
    this.updatingCollaborators = false;
    this.isContentEdited = false;
  }
}
